import { InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import dayjs from 'dayjs';
import { compose } from 'redux';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { LoanType, MIN_YEAR_OF_BUILD } from 'constants/loanApplication';
import { locationTypeOptions, uccpAppraisalUploadStatusOptions } from 'constants/options';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import {
  customDividerStyles,
  customIconStyles,
  customIconWrapperStyles,
  customTitleStyles,
  customTitleWrapperStyles,
} from '../MainContent.styles';
import {
  PROJECT_INFORMATION_MAX_NUMBER,
  PROJECT_INFORMATION_MIN_NUMBER,
} from '../Property/Property.constants';

import {
  unitFieldsBodyWrapperStyles,
  unitFieldsTitleWrapperStyles,
  unitsWrapperStyles,
} from './Appraisal.styles';

const APPRAISAL_ORDER_CONTACTS_GROUP_KEY = 'appraisal-order-contacts';

const APPRAISAL_SURCHARGES_GROUP_KEY = 'appraisal-surcharges';

export const APPRAISAL_CARD_MARKER_GROUP_KEYS = [
  APPRAISAL_ORDER_CONTACTS_GROUP_KEY,
  APPRAISAL_SURCHARGES_GROUP_KEY,
];

const BASE_YEAR = 1900;

export const appraisalConfigFields = {
  appraisalProductTypeId: {
    fieldId: 'appraisalProductTypeId',
    type: FieldType.SELECT,
    nameInModel: 'appraisal.appraisalProductTypeId',
    inputProps: {
      options: [],
      label: 'Appraisal Product',
      placeholder: 'Choose Appraisal Product',
      required: false,
      variant: 'outlined',
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_APPRAISAL_PRODUCT_TYPE_SELECT,
    },
  },
  appraisalUploadStatus: {
    fieldId: 'appraisalUploadStatus',
    type: FieldType.SELECT,
    nameInModel: 'appraisal.uniformCollateralDataPortal.appraisalUploadStatus',
    inputProps: {
      label: 'UCDP Upload Appraisal Status',
      required: false,
      options: uccpAppraisalUploadStatusOptions,
      withSortedOptions: false,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_APPRAISAL_UPLOAD_STATUS_SELECT,
    },
  },
  dataTransmittalDate: {
    fieldId: 'dataTransmittalDate',
    type: FieldType.DATE,
    nameInModel: 'appraisal.uniformCollateralDataPortal.appraisalDataTransmittalDate',
    inputProps: {
      type: FieldInputType.DATE,
      label: 'Appraisal Data Transmittal Date',
      required: false,
      disabled: false,
      variant: 'outlined',
      format: COMMON_DATE_FORMAT,
      'data-test-value': dayjs().set('year', BASE_YEAR),
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_APPRAISAL_TRANSMITTAL_DATE_INPUT,
    },
  },
  appraisalDocumentId: {
    fieldId: 'appraisalDocumentId',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.uniformCollateralDataPortal.appraisalDocumentId',
    inputProps: {
      label: 'Appraisal Document Identifier',
      required: false,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_APPRAISAL_DOCUMENT_ID_INPUT,
    },
  },
  roomsNumber: {
    fieldId: 'roomsNumber',
    type: FieldType.INPUT,
    nameInModel: 'roomsNumber',
    inputProps: {
      label: 'Number of Rooms',
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_NUMBER_OF_ROOMS_INPUT,
      type: FieldInputType.INT,
      minValue: PROJECT_INFORMATION_MIN_NUMBER,
      maxValue: PROJECT_INFORMATION_MAX_NUMBER,
      required: false,
    },
  },
  bedroomsNumber: {
    fieldId: 'bedroomsNumber',
    type: FieldType.INPUT,
    nameInModel: 'bedroomsNumber',
    inputProps: {
      label: 'Number of Bedrooms',
      type: FieldInputType.INT,
      minValue: PROJECT_INFORMATION_MIN_NUMBER,
      maxValue: PROJECT_INFORMATION_MAX_NUMBER,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_NUMBER_OF_BEDROOMS_INPUT,
      required: false,
    },
  },
  bathsNumber: {
    fieldId: 'bathsNumber',
    type: FieldType.INPUT,
    nameInModel: 'bathsNumber',
    inputProps: {
      label: 'Number of Baths',
      type: FieldInputType.INT,
      minValue: PROJECT_INFORMATION_MIN_NUMBER,
      maxValue: PROJECT_INFORMATION_MAX_NUMBER,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_NUMBER_OF_BATHS_INPUT,
      required: false,
    },
  },
  rent: {
    fieldId: 'rent',
    type: FieldType.INPUT,
    nameInModel: 'rent',
    inputProps: {
      label: 'Rent',
      type: FieldInputType.CURRENCY,
      minValue: PROJECT_INFORMATION_MIN_NUMBER,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_UNIT_RENT_INPUT,
      required: false,
    },
  },
  locationType: {
    fieldId: 'locationType',
    type: FieldType.SELECT,
    nameInModel: 'appraisal.neighbourhoodAndSite.locationType',
    inputProps: {
      label: 'Location Type',
      options: locationTypeOptions,
      required: false,
    },
  },
  assessorParcelNumber: {
    fieldId: 'assessorParcelNumber',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.neighbourhoodAndSite.assessorParcelNumber',
    inputProps: {
      label: `Assessor's Parcel Number`,
      disabled: false,
      variant: 'outlined',
      type: FieldInputType.TEXT,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_ASSESSORS_PARCEL_NUMBER_INPUT,
    },
  },
  lot: {
    fieldId: 'lot',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.neighbourhoodAndSite.lot',
    inputProps: {
      label: 'Lot',
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_LOT_INPUT,
      required: false,
    },
  },
  propertyAcreage: {
    fieldId: 'propertyAcreage',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.neighbourhoodAndSite.propertyAcreage',
    inputProps: {
      label: 'Property Acreage',
      type: FieldInputType.FLOAT,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_PROPERTY_ACREAGE_INPUT,
      required: false,
    },
  },
  block: {
    fieldId: 'block',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.neighbourhoodAndSite.block',
    inputProps: {
      label: 'Block',
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_BLOCK_INPUT,
      required: false,
    },
  },
  plat: {
    fieldId: 'plat',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.neighbourhoodAndSite.plat',
    inputProps: {
      label: 'Plat',
      dataTestId: 'plat',
      required: false,
    },
  },
  lotAcquiredDate: {
    fieldId: 'lotAcquiredDate',
    type: FieldType.DATE,
    nameInModel: 'appraisal.neighbourhoodAndSite.lotAcquiredDate',
    inputProps: {
      label: 'Lot Acquired Date',
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_LOT_ACQUIRED_DATE_INPUT,
      format: COMMON_DATE_FORMAT,
      required: false,
    },
  },
  originalLotCost: {
    fieldId: 'originalLotCost',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.neighbourhoodAndSite.originalLotCost',
    inputProps: {
      label: 'Original Cost of Lot',
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_ORIGINAL_LOT_COST_INPUT,
      type: FieldInputType.CURRENCY,
      required: false,
    },
  },
  surveyDate: {
    fieldId: 'surveyDate',
    type: FieldType.DATE,
    nameInModel: 'appraisal.neighbourhoodAndSite.surveyDate',
    inputProps: {
      label: 'Survey Date',
      format: COMMON_DATE_FORMAT,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_SURVEY_DATE_INPUT,
      required: false,
    },
  },
  lotAppraisedValue: {
    fieldId: 'lotAppraisedValue',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.neighbourhoodAndSite.lotAppraisedValue',
    inputProps: {
      label: 'Lot Appraised Value',
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_LOT_APPRAISED_VALUE_INPUT,
      type: FieldInputType.CURRENCY,
      required: false,
    },
  },
  numberOfStories: {
    fieldId: 'numberOfStories',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.neighbourhoodAndSite.numberOfStories',
    inputProps: {
      label: 'Number of Stories',
      type: FieldInputType.INT,
      minValue: 1,
      maxValue: 99,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_NUMBER_OF_STORIES_INPUT,
      required: false,
    },
  },
  propertySquareFootage: {
    fieldId: 'propertySquareFootage',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.propertyDetails.propertySquareFootage',
    inputProps: {
      type: FieldInputType.INT,
      label: 'Property Square Footage',
      required: false,
      disabled: false,
      minValue: 1,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_PROPERTY_SQUARE_FOOTAGE_INPUT,
      'data-test-value': 1,
    },
  },
  censusTract: {
    fieldId: 'censusTract',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.neighbourhoodAndSite.censusTract',
    inputProps: {
      label: 'Census Tract',
      disabled: true,
      variant: 'outlined',
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_CENSUS_TRACT_INPUT,
      required: false,
    },
  },
  yearBuilt: {
    fieldId: 'yearBuilt',
    type: FieldType.DATE,
    nameInModel: 'appraisal.neighbourhoodAndSite.yearBuilt',
    inputProps: {
      label: 'Year Built',
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_YEAR_BUILT_INPUT,
      required: true,
      format: 'YYYY',
      minDate: MIN_YEAR_OF_BUILD,
      type: FieldInputType.YEAR,
      isMinDateBeforeDefault: true,
      views: ['year'],
    },
  },
  appraisalManagementCompanyName: {
    fieldId: 'appraisalManagementCompanyName',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.appraisalManagementCompanyName',
    inputProps: {
      label: 'Appraisal Management Company',
      maxLength: 200,
      required: false,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_APPRAISAL_MANAGEMENT_COMPANY_NAME_INPUT,
    },
  },
  appraiserName: {
    fieldId: 'appraiserName',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.appraiserName',
    inputProps: {
      label: 'Appraiser Name',
      maxLength: 200,
      required: false,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_APPRAISER_NAME_INPUT,
    },
  },
  appraiserLicenseNumber: {
    fieldId: 'appraiserLicenseNumber',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.appraiserLicenseNumber',
    inputProps: {
      label: 'Appraiser License #',
      maxLength: 50,
      required: false,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_APPRAISER_LICENSE_NUMBER_INPUT,
    },
  },
  appraisalCompanyName: {
    fieldId: 'appraisalCompanyName',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.appraisalCompanyName',
    inputProps: {
      label: 'Appraisal Company',
      maxLength: 200,
      required: false,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_APPRAISAL_COMPANY_NAME_INPUT,
    },
  },
  supervisoryAppraiserName: {
    fieldId: 'supervisoryAppraiserName',
    type: FieldType.INPUT,
    nameInModel: 'appraisal.supervisoryAppraiserName',
    inputProps: {
      label: 'Supervisory Appraiser',
      maxLength: 200,
      required: false,
      dataTestId: PropertyAndLoanTestIds.APPRAISAL_FORM_SUPERVISORY_APPRAISER_NAME_INPUT,
    },
  },
};

const getUnitConfigFieldWithFormattedNameInModel = (configField, number) => ({
  ...configField,
  nameInModel: `appraisal.uniformCollateralDataPortal.units[${number}].${configField.nameInModel}`,
  inputProps: {
    ...configField.inputProps,
  },
});

const unitConfigFields = [
  appraisalConfigFields.roomsNumber,
  appraisalConfigFields.bedroomsNumber,
  appraisalConfigFields.bathsNumber,
  appraisalConfigFields.rent,
];

export const getAppraisalConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    numberOfUnits,
    loanType,
    isLoanTBDBundleIssued,
    appraisalProductOptions = [],
    isAppraisalSelectionViewAllowed = false,
    isAppraisalSelectionEditAllowed = false,
  }) => {
    const isMortgageTypeVA = loanType === LoanType.VA;

    const applyEditPermission = entry =>
      entry.fields
        ? { ...entry, fields: entry.fields.map(applyEditPermission) }
        : {
            ...entry,
            inputProps: {
              ...entry.inputProps,
              disabled: !isAppraisalSelectionEditAllowed || !!entry.inputProps?.disabled,
            },
          };

    const appraisalSelectionField = (field, options) => ({
      ...field,
      inputProps: {
        ...field.inputProps,
        options,
        isHidden: !isAppraisalSelectionViewAllowed,
      },
    });

    return {
      groups: [
        ...getConditionalArray(isAppraisalSelectionViewAllowed, [
          {
            type: FieldType.GROUP,
            fields: [
              appraisalSelectionField(
                appraisalConfigFields.appraisalProductTypeId,
                appraisalProductOptions,
              ),
            ],
            key: 'appraisal-selection',
            title: {
              label: 'Appraisal Selection',
              icon: InfoIcon,
              titleStyles: customTitleStyles,
              wrapperStyles: customTitleWrapperStyles,
              iconWrapperStyles: customIconWrapperStyles,
              iconStyles: customIconStyles,
            },
          },
          // Field-less marker groups: Appraisal splits the config here and renders the
          // AppraisalOrderContactsCard and the read-only AppraisalSurchargesCard in their place, so
          // the groups themselves never reach FormGenerator.
          {
            type: FieldType.GROUP,
            fields: [],
            key: APPRAISAL_ORDER_CONTACTS_GROUP_KEY,
          },
          {
            type: FieldType.GROUP,
            fields: [],
            key: APPRAISAL_SURCHARGES_GROUP_KEY,
          },
        ]),
        // condition is here as long as its only field is not required for other loan types
        ...getConditionalArray(isMortgageTypeVA, () => [
          {
            type: FieldType.GROUP,
            fields: [
              ...getConditionalArray(isMortgageTypeVA, [
                appraisalConfigFields.propertySquareFootage,
              ]),
            ],
            key: 'property-details',
            title: {
              label: 'Property Details',
              icon: InfoIcon,
              titleStyles: customTitleStyles,
              wrapperStyles: customTitleWrapperStyles,
              iconWrapperStyles: customIconWrapperStyles,
              iconStyles: customIconStyles,
            },
          },
        ]),
        {
          type: FieldType.GROUP,
          fields: [
            appraisalConfigFields.appraisalUploadStatus,
            appraisalConfigFields.dataTransmittalDate,
            appraisalConfigFields.appraisalDocumentId,
          ],
          key: 'uniform-collateral-data-portal-inputs',
          title: {
            label: 'Uniform Collateral Data Portal (UCDP)',
            icon: InfoIcon,
            titleStyles: customTitleStyles,
            wrapperStyles: customTitleWrapperStyles,
            iconWrapperStyles: customIconWrapperStyles,
            iconStyles: customIconStyles,
          },
        },
        {
          type: FieldType.GROUP,
          fields: Array.from({ length: numberOfUnits }, (_, index) => ({
            type: FieldType.GROUP,
            fields: unitConfigFields.map(configField =>
              getUnitConfigFieldWithFormattedNameInModel(configField, index),
            ),
            customStyles: unitFieldsBodyWrapperStyles,
            title: {
              label: `Unit ${index + 1}`,
              titleStyles: {
                ...customTitleStyles,
                color: ({ palette }) => palette.text.dark,
              },
              wrapperStyles: unitFieldsTitleWrapperStyles,
            },
            key: 'test',
          })),
          key: 'uniform-collateral-data-portal-units',
          title: {
            withDivider: true,
            dividerStyles: customDividerStyles,
          },
          customStyles: unitsWrapperStyles,
        },
        {
          type: FieldType.GROUP,
          fields: [
            appraisalConfigFields.assessorParcelNumber,
            appraisalConfigFields.block,
            appraisalConfigFields.plat,
            appraisalConfigFields.censusTract,
            appraisalConfigFields.locationType,
            appraisalConfigFields.lot,
            appraisalConfigFields.lotAcquiredDate,
            appraisalConfigFields.lotAppraisedValue,
            appraisalConfigFields.numberOfStories,
            appraisalConfigFields.originalLotCost,
            appraisalConfigFields.propertyAcreage,
            appraisalConfigFields.surveyDate,
            ...getConditionalArray(!isLoanTBDBundleIssued, [appraisalConfigFields.yearBuilt]),
          ],
          key: 'neighbourhood-and-site',
          title: {
            label: 'Neighbourhood and Site ',
            icon: InfoIcon,
            titleStyles: customTitleStyles,
            wrapperStyles: customTitleWrapperStyles,
            iconWrapperStyles: customIconWrapperStyles,
            iconStyles: customIconStyles,
          },
        },
        {
          type: FieldType.GROUP,
          fields: [
            appraisalConfigFields.appraisalManagementCompanyName,
            appraisalConfigFields.appraiserName,
            appraisalConfigFields.appraiserLicenseNumber,
            appraisalConfigFields.appraisalCompanyName,
            appraisalConfigFields.supervisoryAppraiserName,
          ],
          key: 'appraisal-contacts',
          title: {
            label: 'Appraisal Contacts',
            icon: InfoIcon,
            titleStyles: customTitleStyles,
            wrapperStyles: customTitleWrapperStyles,
            iconWrapperStyles: customIconWrapperStyles,
            iconStyles: customIconStyles,
          },
        },
      ].map(applyEditPermission),
    };
  },
);
